// Critical-fields contract — see aegle-docs critical_fields_contract.md
import { ReimbursementCreate, ReimbursementStatus } from "@/types";
import {
  validateDateRange,
  validateNotFutureDate,
  validateMinValue,
  validateRequired,
} from "../helpers/validationHelpers";

export const validateReimbursements = (values: ReimbursementCreate): Record<string, string> => {
  const errors: Record<string, string> = {};

  // --- Required Fields (per backend validation) ---
  validateRequired(values.claimant_id_ref, "claimant_id_ref", errors);
  validateRequired(values.status, "status", errors);
  validateRequired(values.requested_amount, "requested_amount", errors);

  // --- Financial Amount Validations ---
  if (values.requested_amount !== undefined) {
    validateMinValue(values.requested_amount, "requested_amount", errors);
  }

  if (values.reimbursement_amount !== undefined) {
    validateMinValue(values.reimbursement_amount, "reimbursement_amount", errors);
  }

  if (values.pended_amount !== undefined) {
    validateMinValue(values.pended_amount, "pended_amount", errors);
  }

  if (values.denial_amount !== undefined) {
    validateMinValue(values.denial_amount, "denial_amount", errors);
  }

  if (values.adjustment_amount !== undefined) {
    // Adjustment amounts can be negative, so only validate they exist if adjustment_reason exists
    if (values.adjustment_reason && values.adjustment_amount === undefined) {
      errors["adjustment_amount"] = "Adjustment amount required when adjustment reason is provided";
    }
  }

  // --- Status-Based Conditional Validation ---

  // Pending status validations
  if (values.status === ReimbursementStatus.PENDING || values.status === ReimbursementStatus.UNDER_REVIEW) {
    validateRequired(
      values.pending_reason,
      "pending_reason",
      errors,
      "Required when status is pending or under review"
    );

    if (values.pended_amount !== undefined && values.pended_amount <= 0) {
      errors["pended_amount"] = "Pended amount must be greater than zero for pending status";
    }
  }

  // Adjusted status validations
  if (values.status === ReimbursementStatus.ADJUSTED) {
    validateRequired(values.adjustment_reason, "adjustment_reason", errors, "Required when status is adjusted");
  }

  // Denied status validations
  if (values.status === ReimbursementStatus.DENIED) {
    validateRequired(values.denial_reason, "denial_reason", errors, "Required when status is denied");

    if (values.denial_amount !== undefined && values.denial_amount <= 0) {
      errors["denial_amount"] = "Denial amount must be greater than zero for denied status";
    }
  }

  // Approved status validations
  if (values.status === ReimbursementStatus.APPROVED) {
    if (values.reimbursement_amount !== undefined && values.reimbursement_amount <= 0) {
      errors["reimbursement_amount"] = "Reimbursement amount must be greater than zero for approved status";
    }
  }

  // Paid status validations
  if (values.status === ReimbursementStatus.PAID) {
    validateRequired(values.reimbursement_amount, "reimbursement_amount", errors, "Required when status is paid");

    if (values.reimbursement_amount !== undefined && values.reimbursement_amount <= 0) {
      errors["reimbursement_amount"] = "Reimbursement amount must be greater than zero for paid status";
    }

    // Paid reimbursements should have check info
    if (!values.check_number && !values.eft) {
      errors["check_number"] = "Check number or EFT indicator required for paid status";
    }

    if (!values.check_date) {
      errors["check_date"] = "Check date required for paid status";
    }
  }

  // --- Date Validations ---

  // Service date range validation
  validateDateRange(values.service_start_date, values.service_end_date, "service", errors);

  // Service start date without end date
  if (values.service_start_date && !values.service_end_date) {
    errors["service_end_date"] = "Service end date required when service start date is provided";
  }

  // Date fields that shouldn't be in the future
  if (values.received_date) {
    validateNotFutureDate(values.received_date, "received_date", errors, "Received date cannot be in the future");
  }

  if (values.check_date) {
    validateNotFutureDate(values.check_date, "check_date", errors, "Check date cannot be in the future");
  }

  if (values.pended_audit_date) {
    validateNotFutureDate(values.pended_audit_date, "pended_audit_date", errors, "Audit date cannot be in the future");
  }

  if (values.adjustment_audit_date) {
    validateNotFutureDate(
      values.adjustment_audit_date,
      "adjustment_audit_date",
      errors,
      "Adjustment audit date cannot be in the future"
    );
  }

  // --- Logical Consistency Checks ---

  // TODO: Cross-resource validation — the sum of reimbursement_amount + pended_amount +
  // adjustment_amount + denial_amount should not exceed the selected claim's total_billed_amount.
  // This requires fetching the claim record (via claim_id_ref) and injecting its total_billed_amount
  // into the form data or running the check in the component's handleSubmit. (ADO follow-up)

  // Reimbursement amount shouldn't exceed requested amount
  if (
    values.requested_amount !== undefined &&
    values.reimbursement_amount !== undefined &&
    values.reimbursement_amount > values.requested_amount
  ) {
    errors["reimbursement_amount"] = "Reimbursement amount cannot exceed requested amount";
  }

  // Sum of pended + denial + reimbursement shouldn't exceed requested
  if (values.requested_amount !== undefined) {
    const pended = values.pended_amount || 0;
    const denied = values.denial_amount || 0;
    const reimbursed = values.reimbursement_amount || 0;
    const total = pended + denied + reimbursed;

    if (total > values.requested_amount) {
      errors["requested_amount"] = "Sum of pended, denied, and reimbursement amounts cannot exceed requested amount";
    }
  }

  // Check date should not be before received date
  if (values.received_date && values.check_date) {
    const receivedDate = new Date(values.received_date);
    const checkDate = new Date(values.check_date);

    if (checkDate < receivedDate) {
      errors["check_date"] = "Check date cannot be before received date";
    }
  }

  // Service dates should not be in the future
  if (values.service_start_date) {
    validateNotFutureDate(
      values.service_start_date,
      "service_start_date",
      errors,
      "Service start date cannot be in the future"
    );
  }

  if (values.service_end_date) {
    validateNotFutureDate(
      values.service_end_date,
      "service_end_date",
      errors,
      "Service end date cannot be in the future"
    );
  }

  // EFT and check number are mutually exclusive-ish (warning, not error)
  if (values.eft && values.check_number) {
    // This might be a warning rather than error - some systems track EFT reference numbers
    // Uncomment if you want this as validation:
    // errors["eft"] = "EFT and check number are typically mutually exclusive";
  }

  return errors;
};
